/**
 * Las Estadísticas de la marca (`/activa/estadisticas`, fase 5c, diseño `Estadisticas.dc.html`). Del equipo, como
 * todo el menú nuevo. Lo que vendieron de más sus campañas en el período elegido (30 días, 90 o todo): los cuatro
 * números, cada grupo con quienes recibieron contra quienes no, y el recorrido del mensaje por canal. Son la suma de
 * los resultados de cada campaña, con las mismas cuentas que su pantalla de Resultado.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { EncabezadoPagina, EstadoVacio, Pagina } from "../../../components/activa/Pagina";
import { usePedido } from "../../emails/hooks/usePedido";
import { btnPrimario } from "../../segmentaciones/components/PasosCampana";
import { R_SEGMENTACIONES } from "../../segmentaciones/logic/segmentar";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { enviosApi } from "../api";
import { RecorridoDelMensaje } from "../components/RecorridoDelMensaje";
import { PERIODOS, anchoSobre, filaDeEstadisticas, tarjetasDeLasEstadisticas, type TonoPildora } from "../logic/estadisticas";
import type { Estadisticas, Periodo } from "../types";

const TARJETA = "rounded-2xl border border-slate-200 bg-white";
const RAMPA = ["#2B2179", "#4735BE", "#695EDE", "#9A91EA", "#C3BDF3"];
const TONO_PILDORA: Record<TonoPildora, string> = { verde: "bg-emerald-50 text-emerald-700", rojo: "bg-red-50 text-red-700", gris: "bg-slate-100 text-slate-600" };
const CANALES = [
  ["email", "Email"],
  ["whatsapp", "WhatsApp"],
] as const;

function PorGrupo({ e }: { e: Estadisticas }) {
  const maximo = Math.max(0, ...e.grupos.flatMap((g) => [g.recibieron.porMil, g.medible ? g.control.porMil : 0]));
  return (
    <section aria-labelledby="estadisticas-por-grupo" className={`${TARJETA} p-5`}>
      <h2 id="estadisticas-por-grupo" className="mb-3 text-lg font-semibold text-slate-900">
        Por grupo
      </h2>
      <ul className="divide-y divide-slate-100">
        {e.grupos.map((g, i) => {
          const f = filaDeEstadisticas(g);
          return (
            <li key={g.nombre} className="grid gap-3 py-3.5 sm:grid-cols-2 xl:grid-cols-[minmax(180px,1.3fr)_minmax(110px,.8fr)_minmax(200px,1.5fr)_minmax(150px,1fr)]" data-testid={`grupo-${g.nombre}`}>
              <span className="flex items-center gap-2.5 font-semibold text-slate-900">
                <span className="inline-block h-3 w-3 flex-none rounded-full" style={{ background: RAMPA[Math.min(i, RAMPA.length - 1)] }} aria-hidden="true" />
                <span className="flex flex-col">
                  <span>{g.nombre}</span>
                  <span className="text-xs font-normal text-slate-500">{f.como}</span>
                </span>
              </span>
              <span className="text-sm">
                <span className="block text-xs text-slate-500">Facturaron</span>
                <strong>{f.facturacion}</strong>
              </span>
              <span className="space-y-1 text-sm">
                <span className="block text-xs text-slate-500">De cada 1.000, compraron</span>
                <span className="flex items-center gap-2">
                  <span className="w-24 text-xs text-slate-500">Recibieron</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <span className="block h-full rounded-full bg-violet-600" style={{ width: anchoSobre(maximo, g.recibieron.porMil) }} />
                  </span>
                  <strong className="w-12 text-right">{f.recibieron}</strong>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-24 text-xs text-slate-500">No recibieron</span>
                  <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <span className="block h-full rounded-full bg-slate-500" style={{ width: g.medible ? anchoSobre(maximo, g.control.porMil) : "0%" }} />
                  </span>
                  <strong className="w-12 text-right">{f.control}</strong>
                </span>
              </span>
              <span className="flex flex-col items-start gap-1.5 text-sm">
                <strong>{f.deMas}</strong>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONO_PILDORA[f.pildora.tono]}`}>{f.pildora.texto}</span>
              </span>
            </li>
          );
        })}
        {e.porSalir.map((g) => (
          <li key={`${g.campana}-${g.nombre}`} className="flex flex-wrap items-center justify-between gap-2 py-3.5 text-sm text-slate-500" data-testid={`por-salir-${g.nombre}`}>
            <span>
              <strong className="text-slate-700">{g.nombre}</strong> · sale por {g.canal === "email" ? "mail" : "WhatsApp"} en «{g.campana}»
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">Todavía sin datos</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function EstadisticasPage() {
  const [periodo, setPeriodo] = useState<Periodo>("30");
  const pedido = usePedido<Estadisticas>(() => enviosApi.estadisticas(periodo), `estadisticas-${periodo}`);
  const e = pedido.dato;
  const vacio = !!e && e.campanas === 0 && e.porSalir.length === 0;
  return (
    <Pagina>
      <EncabezadoPagina
        antetitulo="Estadísticas"
        titulo="Estadísticas"
        bajada="Comparamos a quienes recibieron con un grupo parecido que no recibió nada. La diferencia es lo que vendiste gracias a tus campañas."
        acciones={
          <div role="tablist" aria-label="Período" className="inline-flex gap-1 rounded-xl bg-slate-100 p-1">
            {PERIODOS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={periodo === p.id}
                onClick={() => setPeriodo(p.id)}
                className={`min-h-[40px] rounded-lg px-3.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${periodo === p.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              >
                {p.texto}
              </button>
            ))}
          </div>
        }
      />
      {pedido.error && !e && <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />}
      {!e && !pedido.error && <Esqueleto alto="h-96" etiqueta="Cargando las estadísticas" />}
      {vacio && (
        <EstadoVacio
          icono={BarChart3}
          titulo={periodo === "todo" ? "Todavía no salió ninguna campaña." : "No salió ninguna campaña en este período."}
          testId="sin-estadisticas"
          accion={
            <Link to={R_SEGMENTACIONES} className={btnPrimario}>
              Ver las campañas
            </Link>
          }
        >
          Cuando una campaña sale, acá se suma lo que vendió de más contra su grupo de control.
        </EstadoVacio>
      )}
      {e && !vacio && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="tarjetas">
            {tarjetasDeLasEstadisticas(e).map((t) => (
              <div key={t.titulo} className={`${TARJETA} flex flex-col gap-1 p-5`}>
                <span className="text-sm text-slate-500">{t.titulo}</span>
                <span className="text-2xl font-bold text-slate-900">{t.valor}</span>
                <span className="text-sm text-slate-500">{t.detalle}</span>
              </div>
            ))}
          </div>
          <PorGrupo e={e} />
          <section aria-labelledby="estadisticas-recorrido" className="space-y-3">
            <h2 id="estadisticas-recorrido" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              <span aria-hidden="true">● </span>El recorrido del mensaje
            </h2>
            <div className="grid gap-4 lg:grid-cols-2">
              {CANALES.map(([canal, nombre]) => {
                const r = e.recorrido[canal];
                return (
                  <section key={canal} className={`${TARJETA} space-y-3 p-5`} data-testid={`recorrido-${canal}`}>
                    <h3 className="text-base font-semibold text-slate-900">{nombre}</h3>
                    {r ? <RecorridoDelMensaje recorrido={r} canal={canal} /> : <p className="text-sm text-slate-500">Sin envíos en este período.</p>}
                  </section>
                );
              })}
            </div>
          </section>
        </>
      )}
    </Pagina>
  );
}
